import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Play, Pause, Tv, Music4, ExternalLink, Home } from "lucide-react";
import { toast } from "sonner";
import { usePlayer } from "../context/PlayerContext";
import { getHomeStation, toggleHomeStation } from "../lib/favorite-station";

const STATION_PAGE_ROUTE = {
  fm151: "/fm151",
  vibe: "/vibe",
  tv: "/gfn-tv",
};

const StationCard = ({ station }) => {
  const { current, playing, play, toggle } = usePlayer();
  const isCurrent = current?.id === station.id;
  const isLive = isCurrent && playing;
  const [home, setHome] = useState(() => getHomeStation());
  const isHome = home === station.id;

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === "gfn_home_station") setHome(getHomeStation());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const handleClick = () => {
    if (isCurrent) toggle();
    else play(station.id);
  };

  const handleToggleHome = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const next = toggleHomeStation(station.id);
    setHome(next);
    // notify other cards in the same tab
    window.dispatchEvent(new StorageEvent("storage", { key: "gfn_home_station" }));
    if (next === station.id) {
      toast.success(`${station.name} pinned as your home station.`);
    } else {
      toast(`Home station cleared.`);
    }
  };

  const Icon = station.kind === "video" ? Tv : Music4;

  return (
    <article
      data-testid={`station-card-${station.id}`}
      className="group relative rounded-lg overflow-hidden bg-jet-surface border border-gold/20 hover:border-gold/60 transition-colors duration-300"
      style={{ minHeight: 380 }}
    >
      {/* Background image */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-60 group-hover:opacity-70 group-hover:scale-105 transition-transform duration-700"
        style={{ backgroundImage: `url(${station.image})` }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 to-black/30" />

      <div className="relative z-10 h-full flex flex-col justify-between p-6 md:p-8">
        {/* Top row */}
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-2 border border-gold/30 rounded-full px-3 py-1 backdrop-blur-md bg-black/40">
            <Icon className="w-3.5 h-3.5 text-gold" />
            <span className="font-condensed uppercase tracking-widest text-[10px] text-gold">
              {station.kind === "video" ? "Live TV" : "FM Stream"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {isLive && (
              <div className="relative inline-flex items-center gap-2">
                <span className="relative flex w-3 h-3">
                  <span className="absolute inset-0 rounded-full bg-crimson-live live-ping" />
                  <span className="relative inline-flex w-3 h-3 rounded-full bg-crimson-live live-dot" />
                </span>
                <span className="font-condensed uppercase tracking-widest text-xs text-crimson-live">On Air</span>
              </div>
            )}
            <button
              data-testid={`station-home-${station.id}`}
              onClick={handleToggleHome}
              aria-label={isHome ? "Remove home station" : "Set as home station"}
              title={
                isHome
                  ? "This is your home station — plays first when you open the site."
                  : "Set as your home station"
              }
              className={`w-9 h-9 grid place-items-center rounded-full border backdrop-blur-md transition-colors ${
                isHome
                  ? "border-gold bg-gold/20 text-gold shadow-[0_0_16px_rgba(212,175,55,0.35)]"
                  : "border-white/20 bg-black/40 text-white/70 hover:border-gold hover:text-gold"
              }`}
            >
              <Home className="w-4 h-4" fill={isHome ? "currentColor" : "none"} />
            </button>
          </div>
        </div>

        {/* Title area */}
        <div className="mt-auto">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="font-condensed uppercase tracking-widest text-[11px] text-gold/80">
              {station.genre}
            </div>
            {isHome && (
              <span
                data-testid={`station-home-badge-${station.id}`}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gold/15 border border-gold/40 text-gold font-condensed uppercase tracking-widest text-[9px]"
              >
                <Home className="w-2.5 h-2.5" fill="currentColor" />
                Home
              </span>
            )}
          </div>
          <h3 className="font-display text-4xl md:text-5xl text-white leading-none">
            {station.name}
          </h3>
          <p className="font-body text-neutral-300 text-sm mt-2">{station.tagline}</p>

          <div className="mt-6 flex flex-wrap items-center gap-2">
            <button
              data-testid={`play-${station.id}`}
              onClick={handleClick}
              className="inline-flex items-center gap-3 bg-gold hover:bg-gold-light text-black font-condensed uppercase tracking-widest text-xs px-5 py-3 rounded-full transition-colors"
            >
              {isLive ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{isLive ? "Pause" : "Tune In"}</span>
            </button>
            {STATION_PAGE_ROUTE[station.id] && (
              <Link
                to={STATION_PAGE_ROUTE[station.id]}
                data-testid={`station-open-page-${station.id}`}
                className="inline-flex items-center gap-2 border border-gold/50 hover:border-gold text-gold font-condensed uppercase tracking-widest text-[10px] px-4 py-3 rounded-full transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Open Page
              </Link>
            )}
          </div>
        </div>
      </div>
    </article>
  );
};

export default StationCard;
